import {
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  Typography,
} from "@mui/joy";
import { theme } from "../../../../components/Theme";
import { Trans, useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import OTPInput from "react-otp-input";
import { If, IfElse } from "../../../../components/Condition";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import {
  CountryCode,
  PhNoFormat,
  PhNoWhiteListedCountries,
} from "../../../../utils/constants";
import startsWith from "lodash.startswith";
import { matchesFormat } from "../../../../utils/misc";
import useFormContainer from "../../useFormContainer";
import { formatTime } from "../../../../utils/formatDate";
import { BusinessSessionHelper } from "../../../../helpers/business-session.helper";
import { AuthService } from "../../../../services/auth.service";
import LoadingModal from "../../../../components/LoadingModal/LoadingModal";
import BusinessSelectionModal from "./Signin/BusinessSelectionModal/BusinessSelectionModal";

type Props = {
  formContainer: ReturnType<typeof useFormContainer>;
};

const VerifyOTP = ({ formContainer }: Props) => {
  const { t } = useTranslation();

  const formMethods = useFormContext();
  const { register, getValues, watch } = formMethods;

  const navigate = useNavigate();

  const [isChangePhoneNumber, setIsChangePhoneNumber] = useState(false);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < theme.breakpoints.values.sm
  );

  const {
    changePhoneSeconds,
    phoneNoChangeAttempts,
    maxTries,
    seconds,
    isDisabled,
    isError,
    isLoading,
    handleResendOTPCode,
    handleSubmitOTP,
    setOtpInput,
    otpInput,
    openBusinessSelectionModal,
    closeBusinessModalHandler,
  } = formContainer;

  /**
   * get isMobile on window resize
   */
  useEffect(() => {
    if (window.innerWidth < theme.breakpoints.values.sm) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [window.innerWidth]);

  if (isChangePhoneNumber) {
    return <ChangePhoneNumber callback={() => setIsChangePhoneNumber(false)} />;
  }

  return (
    <>
      {/* container */}
      <Stack
        direction={"column"}
        sx={{
          gap: "16px",
          p: "16px",
          border: `1px solid ${theme.palette.neutral[300]}`,
          borderRadius: "8px",
        }}
      >
        <Typography level="title-md" textColor={theme.palette.primary[500]}>
          {t("landing.verify.title")}
        </Typography>

        <Typography
          level="text-sm-medium"
          textColor={theme.palette.neutral[600]}
        >
          {t("catalog.checkout.steps.2.subtitle")}
        </Typography>

        {/* main section */}
        <Stack
          direction={"column"}
          sx={{ gap: "16px", py: "24px", textAlign: "center" }}
        >
          {/* prompt */}
          <Typography level="text-md-semibold">
            <Trans
              i18nKey={"landing.verify.prompt"}
              values={{
                phoneNumber: `+${getValues(
                  "phoneNumberCountryCode"
                )} ${getValues("phoneNumber")}`,
              }}
            >
              We’ve sent an SMS with an activation code
              <>
                <br />
              </>
              to your mobile no.{" "}
              {`+${getValues("phoneNumberCountryCode")} ${getValues(
                "phoneNumber"
              )}`}
            </Trans>
          </Typography>

          {/* otp input fields */}
          <Stack direction="row" justifyContent="center">
            <OTPInput
              inputType="number"
              containerStyle={{
                width: "100%",
                display: "flex",
                // gap: "0.5rem",
                justifyContent: "center",
              }}
              value={otpInput}
              onChange={setOtpInput}
              numInputs={6}
              renderSeparator={<span className="w-4"></span>}
              renderInput={(props, i) => (
                <input
                  id={"otp-input-" + i}
                  {...props}
                  disabled={isDisabled}
                  className={
                    isError
                      ? "focus:outline-none active:outline-none"
                      : "focus:outline-[#499873] active:outline-[#499873]"
                  }
                  style={{
                    textAlign: "center",
                    borderRadius: "8px",
                    border: `1px solid ${isError ? "#C41C1C" : "#CDD7E1"}`,
                    background: "#FFF",
                    boxShadow: "0px 1px 2px 0px rgba(21, 21, 21, 0.08)",
                    display: "flex",
                    width: isMobile ? "48px" : "52px",
                    height: "64px",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                />
              )}
            />
          </Stack>

          <If condition={isError}>
            <Typography
              level="text-md-regular"
              textColor={theme.palette.danger[500]}
            >
              {t("landing.verify.invalid")}
            </Typography>
          </If>
        </Stack>

        {/* helps */}
        <Stack
          flexDirection={"column"}
          sx={{
            alignItems: "center",
            gap: "16px",
            padding: "16px",
          }}
        >
          {/* retry */}
          <Stack
            direction={"column"}
            sx={{
              gap: "8px",
              alignItems: "center",
            }}
          >
            <Stack flex={1}>
              <Typography
                level="text-sm-regular"
                sx={{
                  maxWidth: "400px",
                  color: theme.palette.common.black,
                }}
              >
                {t("landing.verify.requestAgain")}{" "}
                <span className="text-[#667085]">
                  (
                  {t("landing.verify.requestChances", {
                    chances: maxTries,
                  })}
                  )
                </span>
              </Typography>
              <Typography
                fontSize={16}
                textColor={"text.secondary"}
                fontWeight={600}
              >
                {maxTries === 0 && t("landing.verify.requestChancesNull")}
              </Typography>
            </Stack>
            <IfElse
              condition={seconds !== 0 && maxTries > 0}
              ifBlock={
                <Typography
                  textColor={"text.primary"}
                  fontSize={"16px"}
                  fontWeight={600}
                >
                  {formatTime(seconds)}
                </Typography>
              }
              elseBlock={
                <If condition={maxTries > 0}>
                  <Button
                    onClick={handleResendOTPCode}
                    size="lg"
                    sx={{ margin: "auto" }}
                    variant="plain"
                  >
                    {t("landing.verify.resendBtn")}
                  </Button>
                </If>
              }
            />
          </Stack>

          {/* didn't receive */}
          <Stack
            direction={"column"}
            sx={{
              textAlign: "center",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Typography
              level="text-sm-regular"
              sx={{
                maxWidth: "400px",
                color: theme.palette.common.black,
              }}
            >
              Didn’t receive the OTP? You can update your number below to
              receive the OTP again.{" "}
              <span className="text-[#667085]">
                ({phoneNoChangeAttempts} chance left)
              </span>
            </Typography>
            <IfElse
              //   condition={changePhoneSeconds !== 0 && phoneNoChangeAttempts > 0}
              condition={false}
              ifBlock={
                <Typography level="text-md-semibold">
                  {formatTime(changePhoneSeconds)}
                </Typography>
              }
              elseBlock={
                <IfElse
                  condition={phoneNoChangeAttempts > 0}
                  ifBlock={
                    <Button
                      onClick={() => {
                        setIsChangePhoneNumber(true);
                      }}
                      size="lg"
                      sx={{ margin: "auto" }}
                      variant="plain"
                    >
                      Change Number
                    </Button>
                  }
                  elseBlock={
                    <Button
                      onClick={() => navigate("/signin")}
                      size="lg"
                      sx={{ margin: "auto" }}
                      variant="plain"
                    >
                      Back To Login
                    </Button>
                  }
                />
              }
            />
          </Stack>
        </Stack>
      </Stack>

      {/* terms and service & proceed btn */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        sx={{
          mt: "24px",
          justifyContent: "space-between",
          alignItems: "center",
          py: "8px",
          px: "16px",
          display: { xs: "none", md: "flex" },
        }}
      >
        {/* terms and conditions */}
        <FormControl size="md">
          <Stack
            direction={"row"}
            sx={{
              alignItems: "center",
              width: "100%",
              gap: "12px",
            }}
          >
            <Checkbox
              {...register("agreed")}
              required
              sx={{
                alignSelf: "start",
              }}
            />
            <Typography fontSize={14} fontWeight={400}>
              {t("landing.signup.acceptTerms")}
              <span
                // onClick={() => setShowTermsModal(true)}
                style={{
                  margin: "0 3px",
                  textDecoration: "none",
                }}
              >
                <a
                  href={`${import.meta.env.VITE_TC_URL ?? "#"}`}
                  target="_blank"
                >
                  <span className={"text-[#499873] cursor-pointer font-bold"}>
                    {t("landing.signup.terms")}
                  </span>
                </a>
              </span>
              &
              <span
                // onClick={() => setPrivacyPolicy(true)}
                style={{
                  marginLeft: "3px",
                  textDecoration: "none",
                }}
              >
                <a
                  href={`${import.meta.env.VITE_PP_URL ?? "#"}`}
                  target="_blank"
                >
                  <span className={"text-[#499873] cursor-pointer font-bold"}>
                    {t("landing.signup.privacy")}
                  </span>
                </a>
              </span>
              .
            </Typography>
          </Stack>
        </FormControl>

        {/* proceed btn */}
        <Button
          id="public-checkout-verify-btn"
          sx={{
            maxWidth: { xs: "100%", lg: "100px" },
            ml: "auto",
          }}
          disabled={!watch("agreed") || otpInput.length < 5}
          onClick={() => handleSubmitOTP()}
        >
          {t("commons.proceedBtn")}
        </Button>
      </Stack>

      {/* modal */}
      <LoadingModal isLoading={isLoading} />
      <BusinessSelectionModal
        close={closeBusinessModalHandler}
        isOpen={openBusinessSelectionModal}
      />
    </>
  );
};

const ChangePhoneNumber = ({ callback }: { callback: () => void }) => {
  const { t } = useTranslation();

  const [isValidPhone, setIsValidPhone] = useState(false);

  const methods = useFormContext();
  const {
    setError,
    formState: { errors },
    clearErrors,
    trigger,
    watch,
    setValue,
    getValues,
  } = methods;

  const { isLoading, setIsLoading } = useFormContainer(methods);

  const handleSubmit = async () => {
    setIsLoading(true);
    const data = getValues();
    console.log("SUBMITTED DATA", data);

    try {
      const sendOtpResult = await AuthService.sendFirebaseOtp({
        phoneNumber: data.phoneNumber,
        phoneNumberCountryCode: data.phoneNumberCountryCode,
      });

      if (sendOtpResult?.status === 200) {
        localStorage.setItem("phoneNumber", data.phoneNumber);
        localStorage.setItem("countryCode", data.phoneNumberCountryCode);
        setTimeout(() => {
          callback();
        }, 3000);
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error sending OTP", error);
    }
  };

  return (
    <>
      {/* phone input */}
      <Stack
        direction={"column"}
        sx={{
          gap: "16px",
          p: "16px",
          border: `1px solid ${theme.palette.neutral[300]}`,
          borderRadius: "8px",
        }}
      >
        <Typography level="title-md" textColor={theme.palette.primary[500]}>
          {t("landing.verify.title")}
        </Typography>

        <Stack
          direction={"column"}
          sx={{
            width: "100%",
            gap: "40px",
            py: "40px",
            px: "16px",
          }}
        >
          <Typography level="text-md-semibold">
            {t("landing.signup.enterPhoneNumberPrompt2")}
          </Typography>

          <Stack direction={"row"} gap={2} width={"100%"}>
            <FormControl sx={{ width: "100%" }} error={!!errors.phoneNumber}>
              <FormLabel>{t("profile.address.phoneNumber")}*</FormLabel>
              <PhoneInput
                value={
                  (watch("phoneNumber") &&
                    `+${watch("phoneNumberCountryCode")}${watch(
                      "phoneNumber"
                    )}`) ||
                  ""
                }
                onlyCountries={PhNoWhiteListedCountries}
                enableTerritories={false}
                country={BusinessSessionHelper.GetBusinessCountryID().toLocaleLowerCase()}
                masks={{
                  my: "..-....-....",
                  sg: "....-....",
                  th: "... ... ...",
                  id: "... ... ... ...",
                  mm: ". .... .... .....",
                  au: "... ... ...",
                }}
                isValid={(inputNumber, meta: any, countries) => {
                  if (inputNumber === "" || inputNumber === meta.dialCode) {
                    setIsValidPhone(false);
                    return true;
                  }

                  if (inputNumber.toString().startsWith(`${meta.dialCode}0`)) {
                    if (!errors.phoneNumber?.message) {
                      setError("phoneNumber", {
                        message: t("commons.error.form.mobileInvalidStartZero"),
                      });
                    }
                    setIsValidPhone(false);
                    return false;
                  }

                  const result = (countries || []).some((country: any) => {
                    if (
                      startsWith(inputNumber, country.dialCode) ||
                      startsWith(country.dialCode, inputNumber)
                    ) {
                      const iso2: CountryCode = meta.iso2 as CountryCode;
                      const format = PhNoFormat[iso2];
                      if (!format) {
                        setIsValidPhone(false);
                        return false;
                      }
                      const match = matchesFormat(
                        inputNumber,
                        format,
                        country.countryCode
                      );
                      setIsValidPhone(match);
                      return match;
                    }
                    setIsValidPhone(false);
                    return false;
                  });

                  if (!result && !errors.phoneNumber?.message) {
                    setError("phoneNumber", {
                      message: t("commons.error.form.mobileInvalid"),
                    });
                    setIsValidPhone(false);
                  } else if (result && !!errors.phoneNumber?.message) {
                    if (errors.phoneNumber.type === "manual") {
                      setIsValidPhone(false);
                      return false;
                    }
                    setIsValidPhone(true);
                    clearErrors("phoneNumber");
                    trigger("phoneNumber");
                  }
                  return result;
                }}
                inputStyle={{
                  width: "100%",
                  fontSize: "16px",
                  minHeight: "2.8rem",
                  height: "auto",
                  borderRadius: "8px",
                  fontWeight: 500,
                }}
                buttonStyle={{
                  borderRadius: "8px 0 0 8px",
                }}
                onChange={(phone, meta: any) => {
                  setValue("phoneNumberCountryCode", meta.dialCode);
                  setValue(
                    "phoneNumber",
                    phone.replace(new RegExp(meta.dialCode), "")
                  );
                }}
              />
              <Typography
                level="text-xs-regular"
                textColor={theme.palette.neutral[600]}
                sx={{ mt: "8px" }}
              >
                Ensure your phone number is correct to receive the OTP and
                delivery updates.
              </Typography>
              {errors.phoneNumber?.message && (
                <FormHelperText>
                  {errors.phoneNumber?.message as string}
                </FormHelperText>
              )}
            </FormControl>
          </Stack>

          <Typography
            level="text-sm-regular"
            sx={{
              textAlign: "center",
            }}
          >
            {t("landing.signup.enterPhoneNumberPrompt3")}
          </Typography>
        </Stack>
      </Stack>

      {/* proceed button */}
      <Stack
        direction={"row"}
        sx={{
          justifyContent: "flex-end",
          mt: "24px",
        }}
      >
        <Button
          id="public-checkout-change-phone-number-btn"
          sx={{ maxWidth: "100px" }}
          onClick={handleSubmit}
          disabled={!isValidPhone}
        >
          {t("commons.proceedBtn")}
        </Button>
      </Stack>

      {/* modals */}
      <LoadingModal isLoading={isLoading} />
    </>
  );
};

export default VerifyOTP;
